/**
 * Micromark extension for [[page link]] syntax.
 *
 * This tokenizer parses wiki-style [[Page Title]] links at the character level,
 * BEFORE standard markdown escape processing. This allows us to handle our own
 * escape sequences within the link syntax.
 *
 * ## Syntax
 *
 * Basic link:
 *   [[Page Title]] -> title is "Page Title"
 *
 * Escaped brackets (to include ] in title):
 *   [[Title with \] bracket]] -> title is "Title with ] bracket"
 *
 * Escaped backslash:
 *   [[Title with \\ backslash]] -> title is "Title with \ backslash"
 *
 * Parent/child format (for subpage disambiguation):
 *   [[Parent/Child]] -> parsed as-is, resolution happens at a higher level
 *
 * ## Token Types
 *
 * - `pageLink`: The entire [[...]] construct
 * - `pageLinkMarker`: The [[ opening or ]] closing markers
 * - `pageLinkTitle`: The content between markers (may be multiple tokens if
 *   there are false close attempts with single ])
 *
 * ## Why not use remark-wiki-link?
 *
 * The standard remark-wiki-link package doesn't support:
 * - Backslash escaping of ] within titles
 * - Our Parent/Child hierarchy format
 * - Custom output (it outputs HTML anchors, we need ProseMirror nodes)
 *
 * @see https://github.com/micromark/micromark for micromark documentation
 * @module
 */
import type { Extension } from "micromark-util-types";

/**
 * Character codes used by the tokenizer.
 *
 * Note: micromark preprocesses the input and replaces actual characters
 * with special negative codes for line endings and tabs. See:
 * https://github.com/micromark/micromark/tree/main/packages/micromark-util-symbol
 */
const codes = {
	backslash: 92, // \
	leftBracket: 91, // [
	rightBracket: 93, // ]
	// Micromark special codes (preprocessed from actual characters)
	carriageReturn: -5,
	lineFeed: -4,
	carriageReturnLineFeed: -3,
	eof: null,
} as const;

/**
 * Check if a code represents a line ending.
 * Micromark uses negative codes for preprocessed line endings.
 */
function isLineEnding(code: number | null): boolean {
	return (
		code === codes.lineFeed ||
		code === codes.carriageReturn ||
		code === codes.carriageReturnLineFeed
	);
}

/**
 * Creates a micromark syntax extension for [[page links]].
 *
 * @returns A micromark Extension that can be added to micromarkExtensions
 *
 * @example
 * ```ts
 * import { micromark } from 'micromark';
 * import { pageLinkSyntax } from './micromark-page-link';
 *
 * const result = micromark('[[My Page]]', {
 *   extensions: [pageLinkSyntax()],
 * });
 * ```
 */
export function pageLinkSyntax(): Extension {
	function tokenize(effects: any, ok: any, nok: any) {
		return start;

		/**
		 * Start state: expect first [
		 */

		function start(code: any) {
			if (code !== codes.leftBracket) {
				return nok(code);
			}
			effects.enter("pageLink");
			effects.enter("pageLinkMarker");
			effects.consume(code);
			return openBracket1;
		}

		/**
		 * After first [: expect second [
		 */

		function openBracket1(code: any) {
			if (code !== codes.leftBracket) {
				return nok(code);
			}
			effects.consume(code);
			effects.exit("pageLinkMarker");
			effects.enter("pageLinkTitle");
			return inside;
		}

		/**
		 * Inside the link title: consume until ]] or handle escapes
		 */

		function inside(code: any): any {
			// End of file or line ending - not allowed in page links
			if (code === codes.eof || isLineEnding(code)) {
				return nok(code);
			}

			// Backslash escape - consume backslash and next character
			if (code === codes.backslash) {
				effects.consume(code);
				return insideEscape;
			}

			// Potential closing ]]
			if (code === codes.rightBracket) {
				effects.exit("pageLinkTitle");
				effects.enter("pageLinkMarker");
				effects.consume(code);
				return closeBracket1;
			}

			// Regular character
			effects.consume(code);
			return inside;
		}

		/**
		 * After backslash: consume the escaped character
		 */

		function insideEscape(code: any) {
			// End of file or line ending after backslash - not allowed
			if (code === codes.eof || isLineEnding(code)) {
				return nok(code);
			}
			// Consume escaped character (whatever it is)
			effects.consume(code);
			return inside;
		}

		/**
		 * After first ]: check for second ] to close, or continue as content
		 */

		function closeBracket1(code: any): any {
			if (code === codes.rightBracket) {
				// Found ]] - close the link
				effects.consume(code);
				effects.exit("pageLinkMarker");
				effects.exit("pageLink");
				return ok;
			}
			// Single ] is not a close - it's part of the title content
			// Exit the marker (which only contains the single ]) and re-enter title
			effects.exit("pageLinkMarker");
			effects.enter("pageLinkTitle");
			return inside(code);
		}
	}

	return {
		text: {
			[codes.leftBracket]: {
				tokenize,
			},
		},
	};
}
